import { LEVELS } from '../types'
import { SectionsEditor } from '../components/SectionsEditor'
import type { RoleFormState } from '../lib/role-draft'

/** The short fields at the top of every role. */
type KeyDetails = Pick<RoleFormState, 'title' | 'family' | 'level' | 'industry' | 'location' | 'payMin' | 'payMax' | 'payUnit'>

/**
 * The one form for a role (FR-47), shared by the review step after an
 * upload, a mass upload row before import (FR-58), a new role typed by hand,
 * the role's own edit and a manager's change request, so they can never
 * drift apart.
 *
 * The key details, then the job description section by section. There are
 * no other text boxes: summary, responsibilities and the rest are read from
 * the sections for benchmarking (FR-50) and nobody types them.
 *
 * `lockFamily` is on when a manager is proposing a change: moving a role
 * between families changes what the role is, so it is not one of the fields
 * a manager may propose (FR-17a).
 */
export function RoleForm({
  draft,
  onChange,
  families,
  lockFamily = false,
}: {
  draft: RoleFormState
  onChange: (next: RoleFormState) => void
  families: string[]
  lockFamily?: boolean
}) {
  return (
    <>
      <KeyDetailsFields draft={draft} onChange={(next) => onChange({ ...draft, ...next })} families={families} lockFamily={lockFamily} />
      <hr className="form-rule" />
      <SectionsEditor sections={draft.sections} onChange={(sections) => onChange({ ...draft, sections })} />
    </>
  )
}

/** Title, family, level, industry, location and pay, at the top of the form. */
function KeyDetailsFields({
  draft,
  onChange,
  families,
  lockFamily = false,
}: {
  draft: KeyDetails
  onChange: (next: Partial<KeyDetails>) => void
  families: string[]
  lockFamily?: boolean
}) {
  const set = (field: keyof KeyDetails) => (value: string) => onChange({ [field]: value })
  const hourly = draft.payUnit === 'hour'

  const text = (field: keyof KeyDetails, label: string) => (
    <div className="field">
      <label htmlFor={`jd-${field}`}>
        <span>{label}</span>
      </label>
      <input id={`jd-${field}`} value={draft[field]} onChange={(e) => set(field)(e.target.value)} />
    </div>
  )

  return (
    <div className="grid-2">
      {text('title', 'Title')}

      {/* Typed, not chosen. There is no fixed list of job families: the ones
          this company already uses are offered as suggestions so the same
          family does not end up spelled two ways, and anything else can be
          typed, because a company's next role may not be in a family it has
          used before. */}
      <div className="field">
        <label htmlFor="jd-family">
          <span>Job family</span>
        </label>
        <input
          id="jd-family"
          list="jd-family-options"
          value={draft.family}
          disabled={lockFamily}
          onChange={(e) => set('family')(e.target.value)}
        />
        <datalist id="jd-family-options">
          {families.map((f) => (
            <option key={f} value={f} />
          ))}
        </datalist>
        {lockFamily ? (
          <span className="hint">
            Moving a role between families changes what it is, so HR handles that directly.
          </span>
        ) : (
          families.length > 0 && (
            <span className="hint">Already in use: {families.join(', ')}</span>
          )
        )}
      </div>

      <div className="field">
        <label htmlFor="jd-level">
          <span>Level</span>
        </label>
        <select id="jd-level" value={draft.level} onChange={(e) => set('level')(e.target.value)}>
          {LEVELS.map((l) => (
            <option key={l} value={l}>{l}</option>
          ))}
        </select>
      </div>

      {text('industry', 'Industry')}
      {text('location', 'Location')}
      {/*
        The unit comes before the figures, because it changes what they
        mean. A yearly figure is in thousands and an hourly one is not, and
        a person filling this in top to bottom should know which box they
        are in before they type a number into it.
      */}
      <div className="field">
        <label htmlFor="jd-pay-unit">
          <span>Pay is</span>
        </label>
        <select
          id="jd-pay-unit"
          value={draft.payUnit === 'hour' ? 'hour' : 'year'}
          onChange={(e) => set('payUnit')(e.target.value)}
        >
          <option value="year">Per year</option>
          <option value="hour">Per hour</option>
        </select>
        <span className="hint">
          Nothing is converted. An hourly role keeps its hourly rate.
        </span>
      </div>

      {text('payMin', hourly ? 'Range min (per hour)' : 'Range min (USD thousands)')}
      {text('payMax', hourly ? 'Range max (per hour)' : 'Range max (USD thousands)')}
    </div>
  )
}
